import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Loader2, CheckCircle2, Quote } from "lucide-react";
import { SectionHeading } from "@/components/PackSelection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < n ? "fill-[var(--oz-gold)] text-[var(--oz-gold)]" : "text-[var(--oz-border)]"}`} />
    ))}
  </div>
);

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [state, setState] = useState("idle");
  const [showForm, setShowForm] = useState(false);

  const load = () => axios.get(`${API}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await axios.post(`${API}/reviews`, form);
      setForm({ name: "", rating: 5, message: "" });
      setState("done");
      load();
    } catch {
      setState("idle");
    }
  };

  return (
    <section id="avis" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 md:py-24 scroll-mt-20">
      <SectionHeading eyebrow="Avis clients" title="Ce qu'elles en pensent" />

      {reviews.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={i} data-testid={`review-card-${i}`} className="bg-white rounded-2xl border border-[var(--oz-border)] p-6 relative">
              <Quote className="w-8 h-8 text-[var(--oz-nude)] absolute top-5 right-5" />
              <Stars n={r.rating} />
              <p className="mt-4 text-sm text-[var(--oz-muted)] leading-relaxed">« {r.message} »</p>
              <p className="mt-4 font-serif text-lg text-[var(--oz-text)]">{r.name}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-[var(--oz-muted)]">Soyez la première personne à partager votre avis sur Ozentya.</p>
      )}

      <div className="mt-10 text-center">
        {!showForm && (
          <button
            data-testid="review-open-form"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center px-7 py-3.5 rounded-full border border-[var(--oz-dark)] font-semibold text-sm hover:bg-[var(--oz-dark)] hover:text-white transition-colors"
          >
            Laisser un avis
          </button>
        )}

        {showForm && state === "done" && (
          <div data-testid="review-success" className="inline-flex items-center gap-2 text-[var(--oz-gold)] max-w-md text-center">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> Merci pour votre avis ! Il sera publié après validation par notre équipe.
          </div>
        )}

        {showForm && state !== "done" && (
          <form onSubmit={submit} className="max-w-lg mx-auto bg-white border border-[var(--oz-border)] rounded-2xl p-6 text-left space-y-4">
            <input
              data-testid="review-name"
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Votre prénom"
              className="w-full rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--oz-muted)]">Note :</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  data-testid={`review-star-${n}`}
                  onClick={() => setForm({ ...form, rating: n })}
                >
                  <Star className={`w-6 h-6 ${n <= form.rating ? "fill-[var(--oz-gold)] text-[var(--oz-gold)]" : "text-[var(--oz-border)]"}`} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-message"
              required rows={4} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Votre expérience avec Ozentya..."
              className="w-full rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
            />
            <button
              data-testid="review-submit"
              disabled={state === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors disabled:opacity-60"
            >
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier mon avis"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
